import React from 'react'

import { getClassNames } from '../../../../_utils/classnames'
import { FileUploader } from '@pushly/aqe/lib/components'
import { IFilePickerValue } from '@pushly/aqe/lib/components/file-uploader/interfaces'
import { simpleNotification } from '../../../../_utils/utils'
import classNames from 'classnames'
import { getWrappedMacro, Macro, strIsKnownMacro } from '@pushly/models/lib/enums'
import { Form, Popover, Radio, Switch } from 'antd'
import { FormItemProps } from 'antd/lib/form'
import { GlobalOutlined, StopOutlined } from '@ant-design/icons'
import { EMPTY_MACRO_SENTINEL, MediaFieldSource, MediaFieldSourceKind, asMediaFieldSourceKind } from './media-field-sources'
import DynamicMacroField, { DynamicMacroFieldProps } from './dynamic-macro-field'
import './styles/media-source-field.scss'

interface ITemplateMediaField {
    className?: string
    domainId: number
    accountId?: number
    disabled?: boolean
    defaultDomainMediaUrl?: string
    defaultCompositeValue?: IFilePickerValue
    defaultValue?: string
    value?: string | null
    onChange?: (value: string | undefined) => void
    label?: string | React.ReactNode
    size?: 'small' | 'large' | 'default'
    macroIcon?: React.ReactNode
    macroTag?: string
    macroTagLabel?: string
    hideMacroToggle?: boolean
    mobileFriendly?: boolean

    icon?: string
    iconColor?: string
    pjpg?: boolean
    quality?: number
    cropRatio?: any
    resize?: number[]
    cropDim?: number[]
    cropMax?: number[]
    cropMin?: number[]
    forceCrop?: boolean
    imageType?: string

    formItemProps?: FormItemProps

    // --- opt-in "sources" mode (icon/image fields) ---
    // When `sources` is provided the field renders a `Radio.Group` source selector (when 2+ sources)
    // and emits `onSourceValueChange` instead of the legacy macro-toggle behavior. When omitted, the
    // component behaves exactly as before (app-message / badge consumers are unaffected).
    sources?: MediaFieldSource[]
    initialSource?: MediaFieldSourceKind
    onSourceValueChange?: (next: { source: MediaFieldSourceKind; value: string | null }) => void
    macroPicker?: Pick<
        DynamicMacroFieldProps,
        'placeholder' | 'fieldName' | 'macroTypes' | 'customMacroOptions' | 'secretsMacroOptions' | 'validMacroOptionTypes'
    >
    domainDefaultPreviewUrl?: string
}

const TemplateMediaField = (props: ITemplateMediaField) => {
    const propsChangeTimeout = React.useRef<any>()
    const [tmpValue, setTmpValue] = React.useState<string | undefined>(props.value ?? props.defaultValue)
    const size = !props.size || props.size === 'default' ? 'small' : props.size

    const useSources = !!props.sources?.length
    const showSourceSelector = (props.sources?.length ?? 0) >= 2

    // Active source is genuine UI state in sources mode: an empty `upload` and `none` can both persist
    // as a null value, so the selection cannot be derived from the model alone. Seeded from the
    // parent-derived `initialSource`; resets when the field is remounted (parent keys it per variant).
    const [activeSource, setActiveSource] = React.useState<MediaFieldSourceKind>(
        () => props.initialSource ?? props.sources?.[0]?.key ?? 'upload',
    )
    // Retained values so flipping between sources is non-destructive.
    const lastUpload = React.useRef<string | undefined>(
        props.initialSource === 'upload' ? props.value ?? undefined : undefined,
    )
    const lastMacro = React.useRef<string | undefined>(
        props.initialSource === 'dynamic-macro' ? props.value ?? undefined : undefined,
    )

    const emitUpload = (v: string | undefined) => {
        if (useSources) {
            lastUpload.current = v
            props.onSourceValueChange?.({ source: 'upload', value: v ?? null })
        } else {
            props.onChange?.(v)
        }
    }

    const handleUploadSuccess = (upload?: IFilePickerValue) => {
        const v = upload?.url
        if (v !== tmpValue) {
            setTmpValue(v)
            setTimeout(() => emitUpload(v), 50)
        }
    }

    const handleUploadRemoval = (upload?: IFilePickerValue) => {
        const v = upload?.url
        if (!upload || v === tmpValue) {
            setTmpValue(undefined)
            setTimeout(() => emitUpload(undefined), 50)
        }
    }

    const handleError = (res?: { error?: { error_type?: string; message?: string } }) => {
        const error = res?.error
        if (error && error.error_type === 'invalid_file_type_error') {
            return simpleNotification('error', error.message ?? 'Invalid file type.')
        }
    }

    const handleMacroChange = (value: string) => {
        lastMacro.current = value
        props.onSourceValueChange?.({ source: 'dynamic-macro', value: value || EMPTY_MACRO_SENTINEL })
    }

    const wrappedMacro = props.macroTag ? getWrappedMacro(props.macroTag as Macro) : undefined

    const handleSelectSource = (raw: string | number) => {
        const next = asMediaFieldSourceKind(raw)
        if (!next || next === activeSource) return

        let value: string | null = null
        if (next === 'upload') value = lastUpload.current ?? null
        else if (next === 'dynamic-macro') value = lastMacro.current || EMPTY_MACRO_SENTINEL
        else if (next === 'fixed-macro') value = wrappedMacro ?? null
        // 'domain-default' and 'none' carry a null value (parent sets the appropriate flag).

        setActiveSource(next)
        props.onSourceValueChange?.({ source: next, value })
    }

    // Resets the media value when clear content sets the value to undefined
    React.useEffect(() => {
        if ((props.value === undefined || props.value === null) && !!tmpValue) {
            setTmpValue(undefined)
        }
    }, [props.value === undefined || props.value === null])

    // Handles external reset of value (e.g. segment icon url overrides, AI suggestions)
    React.useEffect(() => {
        clearTimeout(propsChangeTimeout.current)
        propsChangeTimeout.current = setTimeout(() => {
            const valuesMatch = (tmpValue ?? undefined) === (props.value ?? undefined)
            if (props.value && !valuesMatch) {
                setTmpValue(props.value)
            }
        }, 50)
    }, [props.value])

    const valueIsMacroMatch = strIsKnownMacro(tmpValue ?? '', {
        allowedMacros: props.macroTag ? [props.macroTag as Macro] : undefined,
    })

    const renderUploader = () => (
        <FileUploader
            className={props.className}
            modalClassName={classNames({
                mv: props.mobileFriendly,
            })}
            type="image"
            disabled={props.disabled}
            ownerType={props.accountId ? 'org' : 'domain'}
            ownerId={props.accountId ? props.accountId : props.domainId}
            ownerIcon={props.defaultDomainMediaUrl}
            defaultValue={
                props.defaultCompositeValue ??
                (!tmpValue
                    ? undefined
                    : {
                          url: tmpValue,
                      })
            }
            value={
                props.defaultCompositeValue ??
                (!tmpValue
                    ? props.defaultCompositeValue
                    : {
                          url: tmpValue,
                      })
            }
            imageConfig={{
                resize: props.resize,
                cropDim: props.cropDim,
                cropMin: props.cropMin,
                cropMax: props.cropMax,
                cropRatio: props.cropRatio,
                quality: props.quality,
                pjpg: props.pjpg,
            }}
            imageType={props.imageType}
            onSuccess={(upload) => {
                try {
                    handleUploadSuccess(upload)
                } catch (err) {
                    if (err) {
                        console.log(err)
                    }
                }
            }}
            onRemove={(upload) => {
                try {
                    handleUploadRemoval(upload)
                } catch (err) {
                    if (err) {
                        console.log(err)
                    }
                }
            }}
            onError={handleError}
        />
    )

    const renderFixedMacroPlaceholder = () => (
        <div className="dynamic-macro-field">
            <div className="dynamic-macro-field__tile" aria-hidden="true">
                <span className="dynamic-macro-field__glyph">{'{ }'}</span>
                <span className="dynamic-macro-field__tile-label">DYNAMIC</span>
            </div>
        </div>
    )

    if (useSources) {
        const renderSourceBody = () => {
            switch (activeSource) {
                case 'upload':
                    return renderUploader()
                case 'fixed-macro':
                    return renderFixedMacroPlaceholder()
                case 'dynamic-macro':
                    return (
                        <DynamicMacroField
                            value={props.value}
                            onChange={handleMacroChange}
                            disabled={props.disabled}
                            placeholder={props.macroPicker?.placeholder}
                            fieldName={props.macroPicker?.fieldName}
                            macroTypes={props.macroPicker?.macroTypes}
                            customMacroOptions={props.macroPicker?.customMacroOptions}
                            secretsMacroOptions={props.macroPicker?.secretsMacroOptions}
                            validMacroOptionTypes={props.macroPicker?.validMacroOptionTypes}
                        />
                    )
                case 'domain-default':
                    return (
                        <div className="media-source-field__static media-source-field__domain-default">
                            <div className="media-source-field__tile">
                                {props.domainDefaultPreviewUrl ? (
                                    <img src={props.domainDefaultPreviewUrl} alt="Domain default icon" />
                                ) : (
                                    <GlobalOutlined aria-hidden="true" />
                                )}
                            </div>
                        </div>
                    )
                case 'none':
                    return (
                        <div className="media-source-field__static media-source-field__none">
                            <div className="media-source-field__tile media-source-field__tile--muted">
                                <StopOutlined aria-hidden="true" />
                            </div>
                        </div>
                    )
            }
        }

        return (
            <div
                className={getClassNames(null, 'media-field-wrapper', 'media-source-field', `size-${size}`, props.className)}
                data-active-source={activeSource}
            >
                <Form.Item label={props.label} layout="vertical" {...props.formItemProps}>
                    {showSourceSelector && (
                        <Radio.Group
                            className="media-source-field__segmented"
                            size="small"
                            optionType="button"
                            buttonStyle="solid"
                            disabled={props.disabled}
                            value={activeSource}
                            onChange={(ev) => handleSelectSource(ev.target.value)}
                            options={props.sources!.map((source) => ({
                                label: source.tooltip ? (
                                    <Popover content={source.tooltip}>
                                        <span>{source.label}</span>
                                    </Popover>
                                ) : (
                                    source.label
                                ),
                                value: source.key,
                                disabled: source.disabled,
                            }))}
                        />
                    )}
                    <div className="media-source-field__body">{renderSourceBody()}</div>
                </Form.Item>
            </div>
        )
    }

    return (
        <div
            className={getClassNames(null, 'media-field-wrapper', `size-${size}`, props.className, {
                'has-macro-toggle': !props.hideMacroToggle && !!props.macroTag,
            })}
            data-macro={props.macroTag}
        >
            <Form.Item
                label={
                    props.hideMacroToggle ? (
                        props.label
                    ) : (
                        <>
                            <span>{props.label}</span>
                            {!!props.macroTag && (
                                <span className="ecomm-item-image-toggle-wrapper">
                                    <Switch
                                        size="small"
                                        disabled={props.disabled}
                                        checked={valueIsMacroMatch}
                                        onChange={(checked) => {
                                            if (checked && wrappedMacro) {
                                                handleUploadSuccess({ url: wrappedMacro })
                                            } else {
                                                handleUploadRemoval()
                                            }
                                        }}
                                        aria-label={props.macroTagLabel || 'Use macro for image'}
                                    />

                                    {!!props.macroTagLabel && <span>{props.macroTagLabel}</span>}
                                </span>
                            )}
                        </>
                    )
                }
                layout="vertical"
                {...props.formItemProps}
            >
                {valueIsMacroMatch ? (
                    <button
                        name="filestack"
                        className={`filestack-upload ant-upload ant-upload-select ant-upload-select-picture-card image-mode disabled ${props.className}`}
                        disabled={true}
                    >
                        <span className="ant-upload contents" role="button">
                            <div className="faux-btn">
                                {props.macroIcon}
                                <span>{wrappedMacro}</span>
                            </div>
                        </span>
                    </button>
                ) : (
                    renderUploader()
                )}
            </Form.Item>
        </div>
    )
}

export default TemplateMediaField
